import {
	type ReactElement,
	type ReactNode,
	createContext,
} from "react";
import type { ElementProps } from "../functionalComponents/RenderPage";

/**
 * Consecutive bullet elements each render their own single-item <ul> — a
 * screen reader hears "list, 1 item" for every bullet and numbered (#:) items
 * have no ordinal at all. groupListRuns wraps each run of bullets in ONE real
 * <ul>/<ol> (display: contents, so CSS-grid layout is untouched) and the
 * bullet components render <li> roots when inside a run (ListRunContext).
 *
 * Indented (**: / ##:) items become a properly nested list inside a
 * presentational <li>, so top-level ordinals skip them.
 *
 * Autospace blanks interleaved in a run ride along as hidden, unnumbered
 * list items so the vertical rhythm is preserved without breaking the run.
 */

export const ListRunContext = createContext(false);

const TOP_BULLETS = new Set(["*:", "#:"]);
const NESTED_BULLETS = new Set(["**:", "##:"]);
const ORDERED = new Set(["#:", "##:"]);

type El = ReactElement<ElementProps>;

const typeOf = (el: El): string | undefined => el?.props?.type;
const isBullet = (el: El) =>
	TOP_BULLETS.has(typeOf(el) ?? "") || NESTED_BULLETS.has(typeOf(el) ?? "");
const isNested = (el: El) => NESTED_BULLETS.has(typeOf(el) ?? "");
const isBlank = (el: El) => typeOf(el) === "blank:";

/* a blank is only spacing; it stays inside the run as an aria-hidden,
   non-list-item <li> so ol numbering skips it and axe's "lists contain only
   li" rule stays satisfied */
const blankItem = (el: El, key: string) => (
	<li
		aria-hidden="true"
		style={{ display: "block", listStyle: "none" }}
		key={key}
	>
		{el}
	</li>
);

const wrapRun = (run: El[], runKey: string): ReactNode => {
	const firstBullet = run.find(isBullet);
	const ordered = ORDERED.has(typeOf(firstBullet as El) ?? "");
	const Tag = ordered ? "ol" : "ul";

	const items: ReactNode[] = [];
	let i = 0;
	while (i < run.length) {
		const el = run[i];
		if (isBlank(el)) {
			items.push(blankItem(el, `${runKey}-blank-${i}`));
			i++;
			continue;
		}
		if (isNested(el)) {
			// collect the nested sub-run (nested bullets + interior blanks)
			const sub: El[] = [];
			while (i < run.length) {
				if (isNested(run[i])) {
					sub.push(run[i]);
					i++;
				} else if (isBlank(run[i])) {
					let j = i;
					while (j < run.length && isBlank(run[j])) j++;
					if (j < run.length && isNested(run[j])) {
						for (let k = i; k < j; k++) sub.push(run[k]);
						i = j;
					} else break;
				} else break;
			}
			const subOrdered = ORDERED.has(typeOf(sub.find(isBullet) as El) ?? "");
			const SubTag = subOrdered ? "ol" : "ul";
			items.push(
				/* the standard nested-list shape: a plain li containing the
				   sub-list (a role would break the ul>li contract for axe/AT) */
				<li
					style={{ display: "block", listStyle: "none" }}
					key={`${runKey}-nest-${i}`}
				>
					{/* a real box (not display:contents) so the nested list gets
					    its own counter scope — ordinals restart and the outer
					    numbering skips it */}
					<SubTag
						style={{
							margin: 0,
							paddingInlineStart: "2em",
							listStyleType: subOrdered ? "lower-alpha" : "circle",
						}}
					>
						{sub.map((subEl, idx) =>
							isBlank(subEl)
								? blankItem(subEl, `${runKey}-nest-${i}-blank-${idx}`)
								: subEl,
						)}
					</SubTag>
				</li>,
			);
			continue;
		}
		items.push(el);
		i++;
	}

	return (
		/* the run wrapper is a real box sized to the shared text measure (see
		   the qp-list-run rule in DisplayElementsChildren) so bullet text
		   aligns with paragraphs and markers hang into the margin */
		<Tag className="qp-list-run" key={runKey}>
			<ListRunContext.Provider value={true}>{items}</ListRunContext.Provider>
		</Tag>
	);
};

export function groupListRuns(children: El[]): ReactNode[] {
	const out: ReactNode[] = [];
	let i = 0;
	while (i < children.length) {
		if (!isBullet(children[i])) {
			out.push(children[i]);
			i++;
			continue;
		}
		const run: El[] = [children[i]];
		i++;
		while (i < children.length) {
			if (isBullet(children[i])) {
				run.push(children[i]);
				i++;
			} else if (isBlank(children[i])) {
				// blanks stay in the run only when another bullet follows them
				let j = i;
				while (j < children.length && isBlank(children[j])) j++;
				if (j < children.length && isBullet(children[j])) {
					for (let k = i; k < j; k++) run.push(children[k]);
					i = j;
				} else break;
			} else break;
		}
		out.push(wrapRun(run, `list-run-${out.length}`));
	}
	return out;
}
